import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, Stack, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar.jsx';
import skyshift from '../../Images/skyshift.png';
import salesforceImg from '../../workflows/images/salesforce.png';
import oracleImg from '../../workflows/images/oracle.png';
import hubspotImg from '../../workflows/images/hubspot.png';
import sqlImg from '../../workflows/images/sql.png';
import githubImg from '../../workflows/images/github.png';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { selectConnectionsCount } from '../../state/slices/connectionsSlice';
import { useSelector } from 'react-redux';

const IconCard = ({ src, alt, label }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      p: 2,
      borderRadius: 2,
      bgcolor: '#ffffff',
      border: '1px solid',
      borderColor: 'rgba(90, 102, 166, 0.12)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 4px 16px rgba(90, 102, 166, 0.12)',
        transform: 'translateY(-2px)',
        borderColor: 'rgba(90, 102, 166, 0.24)',
      },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 1.5,
        bgcolor: '#F8F9FA',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ width: 32, height: 32, objectFit: 'contain' }}
      />
    </Box>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: '#2C3E50',
        letterSpacing: '0.01em',
      }}
    >
      {label}
    </Typography>
  </Box>
);

IconCard.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  label: PropTypes.string.isRequired,
};

IconCard.defaultProps = {
  alt: 'Connector Icon',
};

const Connector = () => {
  const navigate = useNavigate();

  const connectionsCount = useSelector(selectConnectionsCount);
  // console.log("conn count",connectionsCount);

  const handleCreateConnection = () => {
    navigate('/createconnections');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#F5F7FA' }}>
      <Sidebar />
      <Box sx={{ flex: 1, p: 4 }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          {/* Header */}
          <Box sx={{ mb: 5, textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 1.5,
                color: '#1A2332',
                letterSpacing: '-0.02em',
              }}
            >
              Connect Your Data Sources
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748B',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Seamlessly integrate multiple data sources and destinations
              through our unified platform
            </Typography>
          </Box>

          {/* Main Connection Diagram */}
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 5,
              borderRadius: 3,
              bgcolor: '#ffffff',
              border: '1px solid',
              borderColor: 'rgba(90, 102, 166, 0.08)',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
              overflow: 'visible',
            }}
          >
            {/* Animated connection lines SVG */}
            <Box
              component="svg"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 0,
              }}
              viewBox="0 0 1000 400"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient
                  id="lineGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#5A66A6" stopOpacity="0.5" />
                  <stop offset="50%" stopColor="#5A66A6" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#5A66A6" stopOpacity="0.5" />
                </linearGradient>
                <linearGradient
                  id="oracleLineGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#4CAF50" stopOpacity="0.5" />
                  <stop offset="50%" stopColor="#4CAF50" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#4CAF50" stopOpacity="0.5" />
                </linearGradient>
              </defs>

              {/* Left to Center Lines */}
              <path
                d="M 200 120 L 450 200"
                stroke="url(#lineGradient)"
                strokeWidth="3"
                fill="none"
                strokeDasharray="8 8"
                opacity="0.8"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="16"
                  to="0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>

              <path
                d="M 200 200 L 450 200"
                stroke="url(#lineGradient)"
                strokeWidth="3"
                fill="none"
                strokeDasharray="8 8"
                opacity="0.8"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="16"
                  to="0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>

              <path
                d="M 200 280 L 450 200"
                stroke="url(#lineGradient)"
                strokeWidth="3"
                fill="none"
                strokeDasharray="8 8"
                opacity="0.8"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="16"
                  to="0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>

              {/* Center to Right Lines */}
              <path
                d="M 550 200 L 800 120"
                stroke="url(#lineGradient)"
                strokeWidth="3"
                fill="none"
                strokeDasharray="8 8"
                opacity="0.8"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="16"
                  to="0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>

              <path
                d="M 550 200 L 800 200"
                stroke="url(#lineGradient)"
                strokeWidth="3"
                fill="none"
                strokeDasharray="8 8"
                opacity="0.8"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="16"
                  to="0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>

              <path
                d="M 550 200 L 800 280"
                stroke="url(#lineGradient)"
                strokeWidth="3"
                fill="none"
                strokeDasharray="8 8"
                opacity="0.8"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="16"
                  to="0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>
            </Box>

            {/* Direct Oracle to Oracle Line (highlighted) - Above other elements */}
            <Box
              component="svg"
              sx={{
                position: 'absolute',
                top: '50%',
                left: 0,
                width: '100%',
                height: '4px',
                pointerEvents: 'none',
                zIndex: 10,
                transform: 'translateY(-2px)',
              }}
              viewBox="0 0 1000 4"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient
                  id="oracleDirectLine"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#4CAF50" stopOpacity="0.3" />
                  <stop offset="20%" stopColor="#4CAF50" stopOpacity="0.9" />
                  <stop offset="80%" stopColor="#4CAF50" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#4CAF50" stopOpacity="0.3" />
                </linearGradient>
              </defs>
              <path
                d="M 180 2 L 820 2"
                stroke="url(#oracleDirectLine)"
                strokeWidth="4"
                fill="none"
                strokeDasharray="12 8"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="20"
                  to="0"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </path>
            </Box>

            {/* Sources Column */}
            <Stack
              spacing={2.5}
              sx={{ zIndex: 11, minWidth: 180, position: 'relative' }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: '#64748B',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  mb: 0.5,
                }}
              >
                Sources
              </Typography>
              <IconCard
                src={salesforceImg}
                alt="Salesforce"
                label="Salesforce"
              />
              <IconCard src={oracleImg} alt="Oracle" label="Oracle DB" />
              <IconCard src={hubspotImg} alt="HubSpot" label="HubSpot" />
              <IconCard src={githubImg} alt="GitHub" label="GitHub" />
              <IconCard src={sqlImg} alt="SQL" label="SQL Server" />
            </Stack>

            {/* Center Platform Logo */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                zIndex: 12,
                px: 4,
                position: 'relative',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#F8F9FA',
                  border: '3px solid',
                  borderColor: '#5A66A6',
                  boxShadow: '0 8px 24px rgba(90, 102, 166, 0.15)',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -8,
                    left: -8,
                    right: -8,
                    bottom: -8,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: 'rgba(90, 102, 166, 0.15)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={skyshift}
                  alt="Skyshift"
                  sx={{ width: 64, height: 64, objectFit: 'contain' }}
                />
              </Paper>
              <Typography
                variant="caption"
                sx={{
                  mt: 2,
                  fontWeight: 600,
                  color: '#5A66A6',
                  letterSpacing: '0.05em',
                }}
              >
                ETL PLATFORM
              </Typography>
            </Box>

            {/* Destinations Column */}
            <Stack
              spacing={2.5}
              sx={{ zIndex: 11, minWidth: 180, position: 'relative' }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: '#64748B',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  mb: 0.5,
                }}
              >
                Destinations
              </Typography>
              <IconCard
                src={salesforceImg}
                alt="Salesforce"
                label="Salesforce"
              />
              <IconCard src={oracleImg} alt="Oracle" label="Oracle DB" />
              <IconCard src={hubspotImg} alt="HubSpot" label="HubSpot" />
              <IconCard src={githubImg} alt="GitHub" label="GitHub" />
              <IconCard src={sqlImg} alt="SQL" label="SQL Server" />
            </Stack>
          </Paper>

          {/* CTA Section */}
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={handleCreateConnection}
              sx={{
                bgcolor: '#5A66A6',
                color: '#fff',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(90, 102, 166, 0.25)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#4A5596',
                  boxShadow: '0 6px 20px rgba(90, 102, 166, 0.35)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {connectionsCount > 0
                ? 'Create a New Connection'
                : 'Create Your First Connection'}
            </Button>
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color: '#94A3B8',
                fontSize: '0.875rem',
              }}
            >
              {connectionsCount > 0
                ? 'Lets connect your new data sources'
                : 'Start connecting your data sources in minutes'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Connector;
